// modals/PledgeDiscountsEditor.js
// -----------------------------------------------------------------------------
// Standby (informational) discounts for the registration payment pledge.
//
// A named list of {name, amount, currency} lines entered next to the payment
// plan. They are PRINTED on the pledge (تعهد) and reduce the amount that still
// needs SCHEDULING (remaining-to-schedule + the plan-match check net them out),
// but they do NOT reduce the actual fees/balance owed — the rest is honoured
// later or covered by a donor. Distinct from the "Apply Discount" feature, which
// actually reduces the fees.
//
// Self-contained: seeds its own rows from the `discounts` prop, saves the whole
// list in one PUT (replace-all), then calls onSaved so the parent reloads.
// -----------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Tag, ArrowLeftRight } from 'lucide-react';

const seed = (discounts) =>
  (Array.isArray(discounts) ? discounts : []).map((d) => ({
    name: d.name || '',
    amount: d.amount || 0,
    currency: d.currency === 'LBP' ? 'LBP' : 'USD',
  }));

const PledgeDiscountsEditor = ({
  apiCall, registrationId, discounts, canEdit, planInUSD, usdRate,
  onSaved, showSuccess, showError,
}) => {
  const [lines, setLines] = useState(() => seed(discounts));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-seed whenever the saved discounts change (e.g. after a reload).
  useEffect(() => {
    setLines(seed(discounts));
    setDirty(false);
  }, [discounts]);

  const patch = (i, p) => {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...p } : l)));
    setDirty(true);
  };
  const addLine = () => {
    setLines((ls) => [...ls, { name: '', amount: 0, currency: 'USD' }]);
    setDirty(true);
  };
  const removeLine = (i) => {
    setLines((ls) => ls.filter((_, idx) => idx !== i));
    setDirty(true);
  };
  // Convert an LBP line to USD at the global rate — used when the plan is all in
  // USD, so an LBP standby discount folds into the dollar schedule instead of
  // being left as a separate pound figure.
  const rate = Number(usdRate) > 0 ? Number(usdRate) : 89500;
  const convertToUSD = (i) =>
    patch(i, { amount: Math.round((Number(lines[i].amount) || 0) / rate), currency: 'USD' });

  const save = async () => {
    const clean = lines
      .map((l) => ({
        name: (l.name || '').trim(),
        amount: Number(l.amount) || 0,
        currency: l.currency === 'LBP' ? 'LBP' : 'USD',
      }))
      .filter((l) => l.name && l.amount > 0);
    try {
      setSaving(true);
      await apiCall(`/registration/${registrationId}/pledge-discounts`, {
        method: 'PUT',
        body: JSON.stringify({ discounts: clean }),
      });
      showSuccess && showSuccess('Standby discounts saved');
      setDirty(false);
      onSaved && onSaved();
    } catch (err) {
      showError && showError('Failed to save discounts: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const fmt = (amount, currency) =>
    currency === 'LBP' ? `${Number(amount).toLocaleString()} LL` : `$${Number(amount).toLocaleString()}`;

  // Read-only view for users without edit permission.
  if (!canEdit) {
    if (!lines.length) return null;
    return (
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5 mb-2">
          <Tag size={13} /> Standby discounts (shown on pledge)
        </p>
        <ul className="space-y-1">
          {lines.map((l, i) => (
            <li key={i} className="flex justify-between text-xs text-gray-600">
              <span>{l.name}</span>
              <span className="font-medium text-gray-800">{fmt(l.amount, l.currency)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
          <Tag size={13} /> Standby discounts
        </p>
        <button
          onClick={addLine}
          className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center gap-1"
        >
          <Plus size={12} /> Add discount
        </button>
      </div>
      <p className="text-[11px] text-gray-400 mb-2">
        Printed on the pledge and subtracted from the amount to schedule (the rest
        is covered later / by a donor). Does not change the actual balance owed.
      </p>

      {lines.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-1">No standby discounts.</p>
      ) : (
        <div className="space-y-1.5">
          {lines.map((l, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input
                type="text"
                value={l.name}
                onChange={(e) => patch(i, { name: e.target.value })}
                placeholder="Discount name (e.g. Donor pledge, Staff)"
                className="flex-1 border rounded-lg px-2 py-1.5 text-xs"
              />
              <input
                type="number"
                min="0"
                value={l.amount || ''}
                onChange={(e) => patch(i, { amount: parseFloat(e.target.value) || 0 })}
                placeholder="Amount"
                className="w-24 border rounded-lg px-2 py-1.5 text-xs text-right"
              />
              <select
                value={l.currency}
                onChange={(e) => patch(i, { currency: e.target.value })}
                className="border rounded-lg px-1.5 py-1.5 text-xs bg-white"
              >
                <option value="USD">USD</option>
                <option value="LBP">LBP</option>
              </select>
              {planInUSD && l.currency === 'LBP' && (
                <button
                  onClick={() => convertToUSD(i)}
                  className="text-blue-500 hover:text-blue-700 p-1"
                  title={`Convert to USD @ ${rate.toLocaleString()} LL (plan is all-USD)`}
                >
                  <ArrowLeftRight size={13} />
                </button>
              )}
              <button
                onClick={() => removeLine(i)}
                className="text-red-400 hover:text-red-600 p-1"
                title="Remove"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {dirty && (
        <div className="mt-2 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Save size={13} /> {saving ? 'Saving…' : 'Save discounts'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PledgeDiscountsEditor;
